import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { t } from "$themelocalization";
import Button from "$core-components/Button";
import LoadingDots from "$core-components/LoadingDots";
import CartListModel from "../model/CartListModel";
import CartTooltip from "./CartTooltip";
import { Link } from "react-router-dom";
import urlGenerator from "$ustoreinternal/services/urlGenerator";
import "./CartSummaryButton.scss";

// Order limit constant - orders exceeding this amount cannot checkout
const ORDER_LIMIT = 3000.00;
const ACS_CONTACT_EMAIL = "acs_pubs_assist@acs.org";

const CartSummaryButton = ({ model, loading, setShowAssignToListModal }) => {
  const [isWarningShown, setIsWarningShown] = useState(false);
  const [isErrorShown, setIsErrorShown] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Check if subtotal exceeds the order limit
  const subtotal = model?.summary?.subtotal || 0;
  const isOverOrderLimit = subtotal > ORDER_LIMIT;

  useEffect(() => {
    setIsErrorShown(model?.summary?.hasErrors);
  }, [
    model?.openedList,
    model?.summary?.hasErrors,
    model?.summary?.hasWarnings,
  ]);

  const getCheckoutButtonText = () => {
    if (loading) {
      return <LoadingDots className="summary-checkout-loader" />;
    }
    if (
      model?.isListsMode &&
      model?.openedList?.id === CartListModel.CART_LIST_TYPES.UNASSIGNED
    ) {
      return t("Cart.Summary.ListsCheckoutButton", {
        count: model?.summary?.selectedItemsCount,
      });
    }
    if (
      (model?.isListsMode || model?.isSingleListMode) &&
      model?.openedList?.id !== CartListModel.CART_LIST_TYPES.UNASSIGNED
    ) {
      return t("Cart.Summary.ExportButton", {
        count: model?.summary?.selectedItemsCount,
      });
    }
    return t("Cart.Summary.CheckoutButtonText", {
      count: model?.summary?.selectedItemsCount,
    });
  };

  // Added isOverOrderLimit to disable checkout when over $3000
  const isDisabled =
    isOverOrderLimit ||
    !termsAccepted ||
    model?.summary?.selectedItemsCount === 0 ||
    model?.summary?.totalItemsCount === 0 ||
    model?.summary?.hasErrors ||
    (model?.isListsMode &&
      !model?.openedList?.isUnassigned &&
      !model?.openedList?.isModifiedAfterExport &&
      model?.openedList?.isExported) ||
    (model?.isSingleListMode &&
      !model?.openedList?.isModifiedAfterExport &&
      model?.openedList?.isExported);

  return (
    <div className="summary-btn-container">
      {/* Order Limit Warning Message */}
      {isOverOrderLimit && (
        <div className="order-limit-warning">
          <span>
            For orders greater than ${ORDER_LIMIT.toFixed(2)} please contact ACS.{" "}
            <a href={`mailto:${ACS_CONTACT_EMAIL}`} className="acs-contact-link">
              {ACS_CONTACT_EMAIL}
            </a>
          </span>
        </div>
      )}

      <label className="terms-checkbox-label">
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          className="terms-checkbox"
        />
        <span>
          Please acknowledge reading and accepting the{" "}
          <Link
            to={urlGenerator.get({ page: "terms" })}
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Use
          </Link>{" "}
          before continuing the Checkout process.
        </span>
      </label>

      <Button
        className="button-primary checkout-btn"
        disabled={isDisabled}
        text={getCheckoutButtonText()}
        onClick={async () => {
          if (
            model?.isListsMode &&
            model?.openedList?.id === CartListModel.CART_LIST_TYPES.UNASSIGNED
          ) {
            setShowAssignToListModal(true);
          } else if (
            (model?.isListsMode || model?.isSingleListMode) &&
            model?.openedList?.id !== CartListModel.CART_LIST_TYPES.UNASSIGNED
          ) {
            if (model?.summary?.hasWarnings) {
              setIsWarningShown(true);
              return;
            }
            await model.openedList.export();
          } else if (model?.isWishListMode) {
            if (
              model?.summary?.selectedItemsCount === 0 ||
              model?.summary?.hasErrors ||
              model?.summary?.totalItemsCount === 0
            ) {
              return;
            }
            if (model?.summary?.hasWarnings) {
              setIsWarningShown(true);
              return;
            }
            if (!model?.summary?.hasErrors) {
              model.checkout();
            }
          }
        }}
      />
      <CartTooltip
        onOk={() => {
          if (
            model?.isListsMode &&
            model?.openedList?.id !== CartListModel.CART_LIST_TYPES.UNASSIGNED
          ) {
            setIsWarningShown(false);
            model.openedList.export();
            return;
          }
          if (model?.summary?.hasWarnings) {
            model.checkout();
          }
        }}
        show={isWarningShown || isErrorShown}
        type={model?.summary?.hasErrors ? "error" : "warning"}
        onCancel={() => {
          setIsWarningShown(false);
          setIsErrorShown(false);
        }}
        isListModel={model?.isListsMode}
      />
    </div>
  );
};

export default observer(CartSummaryButton);
